// /admin/settings (architecture sprint-11 §9.5; replaces the sprint-5 Team settings page, A-13). Every
// available key with a global level. Each change saves immediately; errors use the global toast.
import { useState } from 'react';
import { toast } from 'sonner';
import { Page } from '@/components/Page';
import { PageHeader } from '@/components/PageHeader';
import { globalItem } from '@/components/settings/setting-items';
import { SettingGroups } from '@/components/settings/SettingGroups';
import { Skeleton } from '@/components/ui/skeleton';
import { useGlobalSettings, useResetGlobalSetting, useSetGlobalSetting } from '@/hooks/use-settings';
import { isSettingKey } from '@shared/settings-catalogue';

export function GlobalSettingsPage(): JSX.Element {
  const settings = useGlobalSettings();
  const set = useSetGlobalSetting();
  const reset = useResetGlobalSetting();
  const [pendingKey, setPendingKey] = useState<string | null>(null);

  if (!settings.data)
    return (
      <Page width="3xl">
        <Skeleton className="h-64 w-full" />
      </Page>
    );

  function change(key: string, value: unknown): void {
    if (!isSettingKey(key)) return;
    setPendingKey(key);
    set.mutate({ key, value }, { onSuccess: () => toast.success('Setting saved.'), onSettled: () => setPendingKey(null) });
  }

  function resetKey(key: string): void {
    if (!isSettingKey(key)) return;
    setPendingKey(key);
    reset.mutate(key, { onSettled: () => setPendingKey(null) });
  }

  return (
    <Page width="3xl">
      <PageHeader
        title="Global settings"
        hint="Defaults for every project. A project can override them on its Settings tab, and people can choose their own check-in preferences."
      />
      <SettingGroups items={settings.data.map(globalItem)} context="global" onChange={change} onReset={resetKey} pendingKey={pendingKey} />
    </Page>
  );
}
